/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Coerce a session-resolved value into a shape an ad protocol accepts on the wire.
 *
 * **A boolean is never legal on either outbound surface.** `gdpr`, `coppa`, `dnt`
 * and `lmt` are 0/1 integers in OpenRTB (`regs.ext.gdpr`, `regs.coppa`,
 * `device.dnt`, `device.lmt`) and in the IAB URL-parameter convention the VAST path
 * follows — while the session carried several of them as **booleans**
 * (`SessionMetadataDto.user.consent.gdpr` and `.coppa`, retired by `#1526`), and a VMAP
 * extension or a custom field can still hand one over. Passed through untouched
 * they produce `"regs": { "ext": { "gdpr": false } }`, a type violation an exchange
 * may reject outright, and `gdpr=false` on a query string where the convention is
 * `gdpr=0`.
 *
 * `context-value-resolver`'s `flag()` has enforced this **on the macro surface**
 * since `b6c4fed9`, keyed by macro name. It could not cover the other way an
 * operator addresses the same value: a **path** mapping
 * (`session.user.consent.gdpr`) is resolved by `MacroExpressionEngine`, which walks
 * the path and stringifies whatever it finds. So `{gdpr}` sent `0` and
 * `session.user.consent.gdpr` sent `false` — the same field, two authoring forms,
 * two wire values, one of them illegal.
 *
 * That stayed hidden while the console's parameter table pointed at
 * `session.privacy.gdpr`, a branch of the session that does not exist: the path
 * resolved to nothing and the `{gdpr}` in the same row supplied the value. #1003
 * corrected the path, which made the live one start winning — the fix uncovered
 * this rather than causing it, but it is the reason it is now reachable.
 *
 * Applied at the point a resolved value becomes an outbound one, on both surfaces,
 * so neither protocol can disagree about what a session boolean means.
 *
 * Deliberately narrow: only a real `boolean` is touched. An operator's static
 * `"false"` is a string they wrote and is sent as written — the same rule that
 * keeps `007` a string (#923/#940).
 */
export function coerceWireScalar(value: unknown): unknown {
  if (typeof value === 'boolean') return value ? 1 : 0;
  return value;
}

/**
 * A resolved value as an outbound surface can carry it, or `undefined` if it cannot
 * (D23 · R1 — one predicate, named once).
 *
 * **`[object Object]` is not a rendering of anything.** The #1079 survey found four
 * surfaces emitting it, each having independently decided that `String()` was the answer
 * to *"what do I do with a structured value?"* — a question that has no answer without
 * knowing the receiver. So the answer here is *nothing*, and the surface applies its own
 * omit rule; how an object **should** be sent is authored (D23 · R3/R4, "send as").
 *
 * **A list of scalars is representable; an object is not.** That asymmetry is the whole
 * ruling, and it is not convenience: comma-joining a list is the IAB URL convention
 * (`genre=action,drama`), it is what `String(['Thriller'])` already produced, and three
 * modules arrived at it independently. Nothing comparable exists for flattening an
 * object, which is exactly why that one needs an author and this one does not.
 *
 * A **nested** array is dropped: `String([['a']])` is `'a'`, a level of structure
 * discarded with no sign of it — the same guess-dressed-as-passthrough, only harder to
 * notice.
 *
 * Lives here rather than in either caller because #1005 already established this file as
 * *the point a resolved value becomes an outbound one, on both surfaces*, and the defect
 * being fixed is precisely two authoring forms of one value disagreeing.
 */
export function toWireScalar(value: unknown): string | number | boolean | undefined {
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') {
    return value as string | number | boolean;
  }
  if (Array.isArray(value)) {
    const allScalar = value.every((member) => {
      const mt = typeof member;
      return mt === 'string' || mt === 'number' || mt === 'boolean';
    });
    return allScalar ? value.join(',') : undefined;
  }
  return undefined;
}

/**
 * **The value as a list, where it is a flat list of scalars** (`CO4a`, slice `4f`) — the half of
 * {@link toWireScalar}'s ruling that a structured surface can use: that function joins such a list
 * because text is all a query string holds, and this returns the same list unjoined, for the surfaces
 * that can carry one. A nested list or an object is not a flat list, and answers `undefined` here as
 * it does there, so the two can never disagree about which values are representable.
 */
export function asFlatList(value: unknown): readonly (string | number | boolean)[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return toWireScalar(value) === undefined ? undefined : (value as (string | number | boolean)[]);
}

/** The way out wherever a structured value may stand: the one surface that carries structure. */
export const UNREPRESENTABLE_WAY_OUT_BODY =
  'Map it in the ORTB body editor, where a structured value is representable.';

/** Subjects already reported this process, so a per-request read is not a log flood. */
const reportedUnrepresentable = new Set<string>();

/**
 * Say that a value existed and could not be carried — **R2: do not ship the omit without
 * the report.**
 *
 * Dropping a publisher's value silently and correctly is the #880 defect this design
 * exists to prevent, and it is worse than the placeholder it replaces, because
 * `[object Object]` at least appears on the wire where somebody eventually sees it.
 *
 * It states the **outcome**, not the mechanism (#1045): *this went out empty, and here is
 * what it held*. The distinction it protects is the one D12 flagged — an existing message
 * reading *"no value found"* is **false** for a value that resolved to something
 * unrepresentable, and a message that is wrong for half its entries is the defect in the
 * place we just finished cleaning — `constructVastUrl`'s own debug line still reads that
 * way for an object-valued parameter mapping, and cannot tell the two apart, which is why
 * this warning stands beside it rather than being folded into it.
 *
 * `subject` is what the **operator** calls the thing — `{genre}` for a macro, `parameter
 * 'zz_obj'` for a row — because the two authoring forms report through one mechanism for
 * the same reason they resolve through one predicate (#1005). It is also the dedupe key.
 *
 * Reported once per subject per process, the same bound `getSystemDefaults` uses for
 * ignored env overrides: this runs on every ad request, so anything per-call is a flood
 * rather than a warning. The consequence is that it names a *class* of problem rather than
 * an incident, which is the right grain — an unrepresentable mapping is a configuration
 * defect, not a traffic event.
 *
 * **`wayOut` is the surface's own, because the ways out differ** (`#1458`). A parameter row can choose
 * how it is sent; a header refuses that control and can only map one member; a macro may sit on any
 * surface. One sentence for all three told a header's operator to use a control their row does not
 * have (`#1272`). Required, so each caller states what is reachable from where it is.
 */
export function reportUnrepresentable(subject: string, value: unknown, wayOut: string): void {
  if (reportedUnrepresentable.has(subject)) return;
  reportedUnrepresentable.add(subject);

  const shape = Array.isArray(value) ? 'a nested list' : `an object (${Object.keys(value as object).join(', ')})`;
  console.warn(
    `[context] ${subject} held ${shape} and was sent as nothing — a query string, header or pixel ` +
      `carries text, and no flattening is assumed on your behalf. ${wayOut}`,
  );
}
